/**
 * yt-base-landing.js — controller for the BASE landing page (v3, founder
 * 2026-08-28: every base is an ad-ready conversion page).
 *
 * Data: GET /apps/yutrients/bases (this base's recipe + founder-set
 * prices) + GET /apps/yutrients/catalogue (add-on pricing fields, dose
 * grids). Upsell card images come from each ingredient's own product
 * (/products/<slug>.js — the catalogue carries no image URL, same trick
 * as the builder's info modal).
 *
 * Pricing — LOCKSTEP with yt-byo.js computePrices() base mode (which is
 * lockstep with formulator store.ts): ADDITIVE on the founder-set base
 * anchor, no fee line (the base swallows it), no floors.
 *   addonSub  = Σ( price_fee + price_multiple × raw_cost_per_mg × mg )
 *   sub/mo    = base.prices.sub        + addonSub
 *   one-time  = base.prices.one_time   + round2(addonSub × 1.25)
 *   90-day/mo = base.prices.monthly_90d + round2(addonSub × 0.875)
 *
 * ADD TO CART = the funnel's own machinery end-to-end: POST
 * /formula/submit { base_slug, ingredients: ADD-ONS ONLY } → GET
 * /formula/:id/pdp → POST /cart/add.js with the registered bundle lines
 * (90-day twin set / 30-day set / plan-less ':ot' one-time — identical
 * mapping to yt-bundle-pdp.js), then the side cart drawer opens.
 */
(function () {
  'use strict';

  var PROXY = '/apps/yutrients';
  var ONE_TIME_MULT = 1.25;
  var BASIS_90 = 0.875;

  function round2(n) { return Math.round(n * 100) / 100; }
  function money(n) { return '£' + (Math.round(Number(n) * 100) / 100).toFixed(2); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function uuidv4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
  /** mg → display string in the ingredient's own unit (µg below 1mg / mcg unit). */
  function doseDisplay(mg, unit) {
    // bn_cfu probiotics: the number is billions of CFU — checked before the
    // sub-1mg µg fallback (0.5 would otherwise print as "500µg").
    if (String(unit || '').toLowerCase() === 'bn_cfu') return (Math.round(mg * 100) / 100) + ' bn CFU';
    if (unit === 'mcg' || (mg > 0 && mg < 1)) {
      return Math.round(mg * 1000) + 'µg';
    }
    var v = Math.round(mg * 100) / 100;
    if (v >= 1000 && v % 100 === 0) return (v / 1000) + 'g';
    return (Number.isInteger(v) ? v : v.toFixed(2)) + 'mg';
  }

  async function boot() {
    var root = document.querySelector('[data-yt-base-landing]');
    if (!root) return;
    var slug = root.dataset.ytBaseSlug || '';
    var upsellSlugs = (root.dataset.ytUpsellSlugs || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    // BYO hand-off (founder 2026-08-28): the builder sends base blends here
    // with ?blend=<formula_id> — we preload its add-ons + name so this page
    // IS the blend's PDP.
    var blendId = new URLSearchParams(window.location.search).get('blend') || '';
    if (blendId && !/^[0-9a-f-]{36}$/i.test(blendId)) blendId = '';

    // FIRST-PAINT SEED (2026-09-02): the page ships its base + curated
    // upsells + thumbnail URLs inline (seed-landing.ts → product metafield).
    // Without ?blend= nothing needs the network until ADD; a ?blend= hand-off
    // still takes the fetch path because its add-ons can be any ingredient.
    var seed = null;
    try {
      var seedEl = document.querySelector('[data-yt-base-seed]');
      seed = seedEl ? JSON.parse(seedEl.textContent || 'null') : null;
      if (!seed || !seed.base || seed.base.slug !== slug) seed = null;
    } catch (_e) { seed = null; }
    var seedImages = Object.assign({}, window.__ytIngredientImages || {}, (seed && seed.images) || {});

    var base = null, catalogue = null;
    if (seed && !blendId) {
      base = seed.base;
      catalogue = seed.upsells || [];
    }
    var basesRes, catRes, formulaRes = null;
    if (!base) {
      try {
        var fetches = [
          fetch(PROXY + '/bases', { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
          fetch(PROXY + '/catalogue', { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
        ];
        if (blendId) fetches.push(fetch(PROXY + '/formula/' + encodeURIComponent(blendId), { credentials: 'same-origin', headers: { Accept: 'application/json' } }).catch(function () { return null; }));
        var results = await Promise.all(fetches);
        basesRes = results[0]; catRes = results[1]; formulaRes = results[2] || null;
      } catch (_e) { return; }
      if (!basesRes.ok || !catRes.ok) return;
      base = ((await basesRes.json()).bases || []).find(function (b) { return b.slug === slug; });
      catalogue = (await catRes.json()).ingredients || [];
    }
    if (!base) {
      // Held/unknown base — the picker is the safe home.
      window.location.replace('/pages/shop');
      return;
    }
    var catBySlug = new Map(catalogue.map(function (i) { return [i.slug, i]; }));
    var catById = new Map(catalogue.map(function (i) { return [i.id, i]; }));
    var inBase = new Set((base.ingredients || []).map(function (i) { return i.slug; }));

    // ── state ─────────────────────────────────────────────────────────
    // '3mo' | '1mo' | 'onetime' — 30-day default (founder 2026-09-01); the
    // data-yt-default-plan names the plan picked on load (30-day since 3 Nov 2026).
    var plan = root.getAttribute('data-yt-default-plan') || '1mo';
    if (['3mo', '1mo', 'onetime'].indexOf(plan) < 0) plan = '1mo';
    // 21 Oct 2026 (Lucas): the one-time 30-day test. The page sells one tin,
    // bought once, at the SUBSCRIPTION price; every cart line is stamped
    // `_yt_offer: ot30` and the Variant Manager discount Function takes the
    // 20% off (and ships it free) at checkout. No plan picker, no "/mo".
    var OT_MODE = (root.getAttribute('data-yt-offer-mode') || window.__ytOfferMode) === 'one_time_30d';
    if (OT_MODE) plan = 'onetime';
    var addons = []; // [{ ingredient_id, amount_mg }]
    // Preloaded blend (?blend=): its id is reused at ADD as long as the
    // add-ons are untouched; any edit forks a fresh formula (always-fresh
    // rule) carrying the same name.
    var loadedFormulaId = null;
    var loadedName = null;
    var loadedKey = '';
    function addonsKey(list) {
      return list.map(function (a) { return a.ingredient_id + ':' + a.amount_mg; }).sort().join('|');
    }
    if (formulaRes && formulaRes.ok) {
      try {
        var f = await formulaRes.json();
        // The stored snapshot is base + add-ons COMBINED — the add-ons are
        // the rows carrying a NUMERIC price share (Lucas, 21 Sep 2026 — "fix
        // this everywhere"). Same rule the server splits on (formulator
        // src/formula/pdp-payload.ts, isBaseSnapshotRow): a base recipe row
        // never carries a share, every add-on does.
        //
        // This used to subtract by slug (!inBase.has(i.slug)), which threw
        // away a TOP-UP — a second row for an ingredient the base already
        // contains. The customer's extra 100mg of vitamin C vanished from
        // the blend AND from the price, and the moment they touched
        // anything the fresh submit minted a formula without it.
        if (f && f.base_slug === slug) {
          loadedFormulaId = f.id;
          loadedName = (f.stack_name || '').trim() || null;
          // Lucas, 21 Sep 2026: the server now splits this for us (add_ons on GET /formula/:id). Prefer it: the share rule below cannot read a snapshot written by bundle intake, where the base and its add-ons are summed with no markers, and silently yields NOTHING for the 19 live blends in that shape. Fall back to the share rule only for a cached older payload.
          var servedAddOns = Array.isArray(f.add_ons) ? f.add_ons : null;
          addons = (servedAddOns || (f.ingredients || []).filter(function (i) {
            return typeof i.price_share_gbp === 'number';
          }))
            .filter(function (i) { return i.ingredient_id && catById.has(i.ingredient_id); })
            .map(function (i) { return { ingredient_id: i.ingredient_id, amount_mg: Number(i.amount_mg) }; });
          loadedKey = addonsKey(addons);
        }
      } catch (_e) { /* fresh page */ }
    }

    // Resolve the upsell menu: live catalogue rows, not already in the
    // base, priceable, ten of the template's slugs.
    //
    // Lucas, 30 Sep 2026: anything actually IN this blend comes first and is
    // never cut by that limit. A customer asked the AI for mushrooms, got
    // them, could not find them in the carousel and left — the card is how
    // they see the dose, change it, or take it back out. The ten-card cap
    // now applies only to the menu we chose, so the bar is as long as it
    // needs to be.
    var upsells = [];
    var upsellSeen = new Set();
    var upsellMenuCount = 0;
    function addUpsell(ing, inBlend) {
      if (!ing || ing.raw_cost_per_mg == null) return;
      if (upsellSeen.has(ing.slug)) return;
      if (!inBlend && (inBase.has(ing.slug) || upsellMenuCount >= 10)) return;
      if (!inBlend) upsellMenuCount += 1;
      upsellSeen.add(ing.slug);
      upsells.push(ing);
    }
    addons.forEach(function (a) { addUpsell(catById.get(a.ingredient_id), true); });
    upsellSlugs.forEach(function (s) { addUpsell(catBySlug.get(s), false); });

    // ── pricing ───────────────────────────────────────────────────────
    function addonSubTotal() {
      return addons.reduce(function (sum, a) {
        var ing = catById.get(a.ingredient_id);
        if (!ing || ing.raw_cost_per_mg == null) return sum;
        return sum + Number(ing.price_fee || 0) + Number(ing.price_multiple || 0) * Number(ing.raw_cost_per_mg) * a.amount_mg;
      }, 0);
    }
    function lineSub(ing, mg) {
      return Number(ing.price_fee || 0) + Number(ing.price_multiple || 0) * Number(ing.raw_cost_per_mg) * mg;
    }
    function basisMult() { return plan === 'onetime' ? (OT_MODE ? 1 : ONE_TIME_MULT) : plan === '3mo' ? BASIS_90 : 1; }
    function prices() {
      var addSub = addonSubTotal();
      var out = {
        sub: round2(Number(base.prices.sub) + round2(addSub)),
        one_time: round2(Number(base.prices.one_time) + round2(addSub * ONE_TIME_MULT)),
        m90: round2(Number(base.prices.monthly_90d) + round2(addSub * BASIS_90)),
      };
      // One-time 30-day test: the one-time figure shown is the sub price.
      if (OT_MODE) out.one_time = out.sub;
      return out;
    }

    // ── offer picker ──────────────────────────────────────────────────
    var offers = Array.prototype.slice.call(root.querySelectorAll('[data-yt-plan]'));
    offers.forEach(function (label) {
      label.addEventListener('click', function () {
        plan = label.getAttribute('data-yt-plan');
        render();
      });
    });

    function setText(sel, text) {
      root.querySelectorAll(sel).forEach(function (el) { el.textContent = text; });
    }

    // ── blend state for other sections (30 Oct 2026) ───────────────────
    // The v7 PDP's ingredient block ("See all N ingredients" + the Nutrition
    // Information popup) reads the CURRENT blend — base rows plus whatever
    // add-ons are in — from window.__ytBlend and listens for 'yt:blend'.
    function publishBlend() {
      var rows = (base.ingredients || []).map(function (i) {
        return { slug: i.slug, name: i.name, panel_name: i.panel_name || i.name, dose_mg: Number(i.dose_mg), dose_display: i.dose_display, nrv_pct: i.nrv_pct == null ? null : Number(i.nrv_pct), source: 'base' };
      });
      addons.forEach(function (a) {
        var ing = catById.get(a.ingredient_id); if (!ing) return;
        var mg = Number(a.amount_mg);
        var nrv = ing.nrv_mg ? Math.round(mg / Number(ing.nrv_mg) * 100) : null;
        rows.push({ slug: ing.slug, name: ing.name, panel_name: ing.tagline || ing.name, dose_mg: mg, dose_display: doseDisplay(mg, ing.unit).replace(/(\d)(mg|g|µg)$/, '$1 $2'), nrv_pct: nrv, source: 'addon' });
      });
      var grams = rows.reduce(function (s, r) { return s + (isFinite(r.dose_mg) ? r.dose_mg : 0); }, 0) / 1000;
      window.__ytBlend = { base_slug: base.slug, base_name: base.name, tin_name: base.tin_name || base.name, count: rows.length, grams: Math.round(grams * 100) / 100, ingredients: rows };
      try { document.dispatchEvent(new CustomEvent('yt:blend', { detail: window.__ytBlend })); } catch (_e) { /* old browsers: the popup reads window.__ytBlend on open */ }
    }

    // ── contents rows ─────────────────────────────────────────────────
    var rowsEl = root.querySelector('[data-yt-inside-rows]');
    function renderRows() {
      // Founder 2026-09-03: each base ingredient shows its share of the base
      // price ("£2.27") so the blend reads as many reasonably priced things.
      var baseCount = (base.ingredients || []).length || 1;
      var basePriceAtBasis = plan === 'onetime' ? Number(OT_MODE ? base.prices.sub : base.prices.one_time) : plan === '3mo' ? Number(base.prices.monthly_90d) : Number(base.prices.sub);
      var baseShare = round2(basePriceAtBasis / baseCount);
      // The last row absorbs the rounding remainder so the shares sum to the base price.
      var lastShare = round2(basePriceAtBasis - baseShare * (baseCount - 1));
      var html = (base.ingredients || []).map(function (i, idx) {
        var share = idx === baseCount - 1 ? lastShare : baseShare;
        return '<li class="yt-base-landing__row">' +
          '<span class="yt-base-landing__row-thumb" data-yt-row-slug="' + esc(i.slug || '') + '" aria-hidden="true"></span>' +
          '<span class="yt-base-landing__row-name">' + esc(i.name) + '</span>' +
          '<span class="yt-base-landing__row-addprice" data-yt-row-share>' + esc(money(share)) + (plan === 'onetime' ? '' : '/mo') + '</span>' +
          '<span class="yt-base-landing__row-dose">' + esc(i.dose_display) + '</span>' +
        '</li>';
      }).join('');
      html += addons.map(function (a) {
        var ing = catById.get(a.ingredient_id);
        if (!ing) return '';
        var min = Number(ing.min_dose_mg) || Number(ing.dose_step_mg) || 1;
        var max = Number(ing.max_dose_mg) || min;
        var priceAtBasis = round2(lineSub(ing, a.amount_mg) * basisMult());
        // Founder 2026-08-31: price sits BESIDE the name in a subtle face,
        // the "Added" pill is gone, and the dose control is one unbroken
        // feature styled like the customise page's selector.
        return '<li class="yt-base-landing__row yt-base-landing__row--addon" data-yt-addon-row="' + esc(ing.id) + '">' +
          '<span class="yt-base-landing__row-thumb" data-yt-row-slug="' + esc(ing.slug || '') + '" aria-hidden="true"></span>' +
          '<span class="yt-base-landing__row-name">' + esc(ing.name) + '</span>' +
          '<span class="yt-base-landing__row-addprice">+' + esc(money(priceAtBasis)) + (plan === 'onetime' ? '' : '/mo') + '</span>' +
          '<span class="yt-base-landing__row-controls">' +
            '<span class="yt-base-landing__step">' +
              '<button type="button" class="yt-base-landing__step-btn" data-yt-step="-1" aria-label="Decrease dose"' + (a.amount_mg <= min ? ' disabled' : '') + '>&minus;</button>' +
              '<span class="yt-base-landing__step-val">' + esc(doseDisplay(a.amount_mg, ing.unit)) + '</span>' +
              '<button type="button" class="yt-base-landing__step-btn" data-yt-step="1" aria-label="Increase dose"' + (a.amount_mg >= max ? ' disabled' : '') + '>+</button>' +
            '</span>' +
            '<button type="button" class="yt-base-landing__row-remove" data-yt-remove aria-label="Remove ' + esc(ing.name) + '">&times;</button>' +
          '</span>' +
        '</li>';
      }).join('');
      // Total row: "N products, one scoop · £X/mo · £Y a day".
      var totals = prices();
      var shownTotal = plan === 'onetime' ? totals.one_time : plan === '3mo' ? totals.m90 : totals.sub;
      var countAll = (base.ingredients || []).length + addons.length;
      html += '<li class="yt-base-landing__row yt-base-landing__row--total">' +
        '<span class="yt-base-landing__row-total-count">' + countAll + ' products, one scoop</span>' +
        '<span class="yt-base-landing__row-total-figures">' +
          '<span class="yt-base-landing__row-total-price">' + esc(money(shownTotal)) + (plan === 'onetime' ? '' : '/mo') + '</span>' +
          '<span class="yt-base-landing__row-total-day">' + esc(money(round2(shownTotal / 30))) + ' a day</span>' +
        '</span>' +
      '</li>';
      rowsEl.innerHTML = html;
      paintRowThumbs();
      var count = (base.ingredients || []).length + addons.length;
      setText('[data-yt-inside-count]', '· ' + count + ' ingredients, one scoop');
      publishBlend();
      setText('[data-yt-sticky-day]', '· ' + money(round2(shownTotal / 30)) + ' a day');

      // Wire steppers + removes.
      rowsEl.querySelectorAll('[data-yt-addon-row]').forEach(function (row) {
        var id = row.getAttribute('data-yt-addon-row');
        var addon = addons.find(function (a) { return a.ingredient_id === id; });
        var ing = catById.get(id);
        if (!addon || !ing) return;
        row.querySelectorAll('[data-yt-step]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var step = Number(ing.dose_step_mg) || 1;
            var min = Number(ing.min_dose_mg) || step;
            var max = Number(ing.max_dose_mg) || min;
            var next = addon.amount_mg + step * Number(btn.getAttribute('data-yt-step'));
            if (next < min) next = min;
            if (next > max) next = max;
            addon.amount_mg = next;
            render();
          });
        });
        var rm = row.querySelector('[data-yt-remove]');
        if (rm) rm.addEventListener('click', function () {
          addons = addons.filter(function (a) { return a.ingredient_id !== id; });
          render();
        });
      });
    }

    // ── upsell grid ───────────────────────────────────────────────────
    var upsellWrap = root.querySelector('[data-yt-upsells]');
    var gridEl = root.querySelector('[data-yt-upsell-grid]');
    var imageCache = new Map();
    // One in-flight fetch per slug, shared by the row thumbs and the
    // carousel cards (rows re-render on every dose/plan change, so a
    // cached PROMISE — not just the result — is what stops refetching).
    var photoPromises = new Map();
    function fetchProductImage(handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { return (p && (p.featured_image || (p.images && p.images[0]))) || null; })
        .catch(function () { return null; });
    }
    function photoSrc(slug) {
      // Baked thumbnail map first (seed / shop metafield) — no request at all.
      if (seedImages[slug]) return Promise.resolve(seedImages[slug]);
      if (!photoPromises.has(slug)) {
        // Marketing page first (handle = slug); a few ingredients have no
        // marketing product (e.g. spirulina, wheatgrass), so fall back to
        // their component product, which always exists.
        photoPromises.set(slug, fetchProductImage(slug).then(function (src) {
          return src || fetchProductImage('component-' + slug);
        }));
      }
      return photoPromises.get(slug);
    }
    /** Paint every row's circular thumb (founder 2026-08-31). */
    function paintRowThumbs() {
      root.querySelectorAll('[data-yt-row-slug]').forEach(function (el) {
        var slug = el.getAttribute('data-yt-row-slug');
        if (!slug || el.dataset.painted === '1') return;
        photoSrc(slug).then(function (src) {
          if (src) { el.style.backgroundImage = 'url(' + src + ')'; el.dataset.painted = '1'; }
        });
      });
    }
    function loadCardImage(ing, imgEl) {
      photoSrc(ing.slug).then(function (src) { if (src) imgEl.src = src; });
    }
    function buildUpsells() {
      if (!upsells.length || !gridEl) return;
      upsellWrap.hidden = false;
      gridEl.innerHTML = upsells.map(function (ing) {
        return '<div class="yt-base-landing__card" data-yt-card="' + esc(ing.id) + '">' +
          '<img class="yt-base-landing__card-img" alt="' + esc(ing.name) + '" loading="lazy">' +
          '<span class="yt-base-landing__card-top"><span class="yt-base-landing__card-price" data-yt-card-price></span></span>' +
          '<div class="yt-base-landing__card-body">' +
            '<span class="yt-base-landing__card-name">' + esc(ing.name) + '</span>' +
            '<span class="yt-base-landing__card-dose">' + esc(doseDisplay(Number(ing.default_dose_mg), ing.unit)) + ' daily</span>' +
            '<button type="button" class="yt-base-landing__card-add" data-yt-card-add>ADD</button>' +
          '</div>' +
        '</div>';
      }).join('');
      gridEl.querySelectorAll('[data-yt-card]').forEach(function (card) {
        var id = card.getAttribute('data-yt-card');
        var ing = catById.get(id);
        if (!ing) return;
        loadCardImage(ing, card.querySelector('img'));
        card.querySelector('[data-yt-card-add]').addEventListener('click', function () {
          var existing = addons.find(function (a) { return a.ingredient_id === id; });
          if (existing) {
            addons = addons.filter(function (a) { return a.ingredient_id !== id; });
          } else {
            addons.push({ ingredient_id: id, amount_mg: Number(ing.default_dose_mg) });
            if (window.ytTrack) { try { ytTrack('base_upsell_added', { base: slug, ingredient: ing.name }); } catch (_e) {} }
            // v4: the rows live in an accordion — open it so the new line is seen.
            var accIng = root.querySelector('[data-yt-acc-ingredients]');
            if (accIng && !accIng.open) accIng.open = true;
          }
          render();
        });
      });
    }
    function renderUpsellState() {
      if (!gridEl) return;
      gridEl.querySelectorAll('[data-yt-card]').forEach(function (card) {
        var id = card.getAttribute('data-yt-card');
        var ing = catById.get(id);
        if (!ing) return;
        var added = addons.some(function (a) { return a.ingredient_id === id; });
        card.classList.toggle('is-added', added);
        card.querySelector('[data-yt-card-add]').textContent = added ? 'ADDED ✓' : 'ADD';
        var priceEl = card.querySelector('[data-yt-card-price]');
        var p = round2(lineSub(ing, Number(ing.default_dose_mg)) * basisMult());
        priceEl.textContent = '+' + money(p) + (plan === 'onetime' ? '' : '/mo');
      });
    }

    // ── full render ───────────────────────────────────────────────────
    var stickyPrice = root.querySelector('[data-yt-sticky-price]');
    var stickyUnit = root.querySelector('[data-yt-sticky-unit]');
    var stickySub = root.querySelector('[data-yt-sticky-sub]');
    var stickySelect = root.querySelector('[data-yt-sticky-select]');
    var PLAN_LABELS = { '3mo': '90-Day Supply (Save 30%)', '1mo': '30-Day Supply (Save 20%)', onetime: OT_MODE ? '30-Day Supply' : 'One-time purchase' };
    if (stickySelect) {
      stickySelect.addEventListener('change', function () {
        plan = stickySelect.value;
        render();
      });
    }
    // v7 sticky (30 Oct 2026): a two-way segmented control on desktop.
    var stickySeg = Array.prototype.slice.call(root.querySelectorAll('[data-yt-sticky-seg] button'));
    stickySeg.forEach(function (b) {
      b.addEventListener('click', function () { plan = b.getAttribute('data-plan'); render(); });
    });
    function render() {
      var p = prices();
      setText('[data-yt-price-90]', money(p.m90));
      setText('[data-yt-serving-90]', money(round2(p.m90 / 30)));
      setText('[data-yt-total-90]', money(round2(p.m90 * 3)));
      setText('[data-yt-total-90-again]', money(round2(p.m90 * 3)));
      setText('[data-yt-price-30]', money(p.sub));
      setText('[data-yt-total-30]', money(p.sub));
      setText('[data-yt-serving-30]', money(round2(p.sub / 30)));
      setText('[data-yt-price-ot]', money(p.one_time));
      setText('[data-yt-price-ot2]', money(p.one_time));
      setText('[data-yt-serving-ot]', money(round2(p.one_time / 30)));
      // v7 fields (30 Oct 2026): per-night figures and the struck 90-day "was".
      setText('[data-yt-night-90]', money(round2(p.m90 / 30)));
      setText('[data-yt-night-30]', money(round2(p.sub / 30)));
      setText('[data-yt-night-ot]', money(round2(p.one_time / 30)));
      setText('[data-yt-was-90]', money(round2(p.one_time * 3)));
      offers.forEach(function (label) {
        var match = label.getAttribute('data-yt-plan') === plan;
        label.classList.toggle('yt-formula-pdp__offer--selected', match);
        // v7 (3 Nov 2026): the chosen card's border goes black; the 30-day
        // label sits inside its banded card, the others are the card.
        var card = label.closest('.ybp7-card');
        if (card) card.classList.toggle('is-selected', match);
        var radio = label.querySelector('input[type="radio"]');
        if (radio) radio.checked = match;
      });
      // 27 Sep 2026: on the 90-day plan the sticky bar shows what is
      // charged — the quarter, every 90 days — never "£39.33/mo".
      if (stickyPrice) {
        stickyPrice.textContent = plan === 'onetime' ? money(p.one_time) : plan === '3mo' ? money(round2(p.m90 * 3)) : money(p.sub);
      }
      if (stickyUnit) stickyUnit.textContent = plan === 'onetime' ? '' : plan === '3mo' ? ' every 90 days' : '/mo';
      // v4 (1 Oct 2026): the Checkout button carries what is charged today,
      // and the sticky bar's plan dropdown mirrors the cards.
      var chargedToday = plan === 'onetime' ? p.one_time : plan === '3mo' ? round2(p.m90 * 3) : p.sub;
      setText('[data-yt-cta-price]', money(chargedToday));
      var perServing = plan === 'onetime' ? p.one_time / 30 : plan === '3mo' ? p.m90 / 30 : p.sub / 30;
      if (stickySub) stickySub.textContent = money(round2(perServing)) + ' / day';
      if (stickySelect && stickySelect.value !== plan) stickySelect.value = plan;
      var stickyOpt = stickySelect && stickySelect.selectedIndex >= 0 ? stickySelect.options[stickySelect.selectedIndex].text : '';
      setText('[data-yt-sticky-plan]', stickyOpt || PLAN_LABELS[plan] || '');
      stickySeg.forEach(function (b) { var on = b.getAttribute('data-plan') === plan; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on)); });
      renderRows();
      renderUpsellState();
      if (ctls) paintCta();
    }

    /**
     * Engrave the blend's name onto the tin artwork (founder 2026-08-31:
     * "the image does not have the name they chose"). Same fit buckets as
     * the standard blend PDP so a long name shrinks the whole lockup
     * rather than clipping. No blend loaded → the spans stay empty and the
     * band shows the brand alone, as ad traffic should see it.
     */
    function paintTinName(name) {
      var shown = String(name || '').trim();
      if (!shown) return;
      var fit = shown.length <= 12 ? 'is-fit-1'
        : shown.length <= 15 ? 'is-fit-2'
        : shown.length <= 18 ? 'is-fit-3'
        : shown.length <= 22 ? 'is-fit-4' : 'is-fit-5';
      root.querySelectorAll('.yt-formula-pdp__tin-overlay').forEach(function (ov) {
        ov.classList.remove('is-fit-1', 'is-fit-2', 'is-fit-3', 'is-fit-4', 'is-fit-5');
        ov.classList.add(fit);
        var dyn = ov.querySelector('.yt-formula-pdp__tin-overlay-text--dynamic');
        if (dyn) dyn.textContent = shown;
      });
    }

    // ── gallery ───────────────────────────────────────────────────────
    var views = Array.prototype.slice.call(root.querySelectorAll('[data-yt-stage-view]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-yt-thumb]'));
    var stageEl = root.querySelector('.yt-formula-pdp__stage');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-yt-dot]'));
    function markActive(i) {
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-yt-thumb') === String(i)); });
      dots.forEach(function (d) { d.classList.toggle('is-active', d.getAttribute('data-yt-dot') === String(i)); });
    }
    function showView(i) {
      views.forEach(function (v) { v.classList.toggle('is-shown', v.getAttribute('data-yt-stage-view') === String(i)); });
      markActive(i);
      // Phones (29 Sep 2026): the stage is a swipe carousel with every slide
      // laid out, so a thumbnail tap scrolls to its slide instead.
      if (stageEl && stageEl.scrollWidth > stageEl.clientWidth + 2) {
        var target = views[Number(i)];
        if (target) stageEl.scrollTo({ left: target.offsetLeft - stageEl.offsetLeft, behavior: 'smooth' });
      }
    }
    // …and a swipe moves the active thumbnail.
    if (stageEl) {
      var syncT;
      stageEl.addEventListener('scroll', function () {
        clearTimeout(syncT);
        syncT = setTimeout(function () {
          if (stageEl.scrollWidth <= stageEl.clientWidth + 2) return;
          var left = stageEl.scrollLeft, best = 0, bestD = Infinity;
          views.forEach(function (v, k) { var d = Math.abs((v.offsetLeft - stageEl.offsetLeft) - left); if (d < bestD) { bestD = d; best = k; } });
          markActive(best);
        }, 80);
      }, { passive: true });
    }
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showView(t.getAttribute('data-yt-thumb')); });
    });
    var prevBtn = root.querySelector('[data-yt-thumbs-prev]');
    var nextBtn = root.querySelector('[data-yt-thumbs-next]');
    function currentView() {
      var active = views.findIndex(function (v) { return v.classList.contains('is-shown'); });
      if (active >= 0) return active;
      var t = thumbs.findIndex(function (b) { return b.classList.contains('is-active'); });
      return t < 0 ? 0 : t;
    }
    var nViews = views.length || 1;
    function stepView(dir) { showView((currentView() + nViews + dir) % nViews); }
    // 16 Oct 2026 (Lucas: "they only try to load when I click"): on desktop
    // the hidden slides are display:none AND loading=lazy, and a browser
    // never fetches a lazy image it cannot see — so every slide downloaded
    // on its first click. Once the page has loaded, warm them all: flip
    // each to eager and touch its URL so the click is instant.
    function warmSlides() {
      views.forEach(function (v) {
        var img = v.tagName === 'IMG' ? v : v.querySelector('img');
        if (!img || !img.getAttribute('src')) return;
        if (img.loading === 'lazy') img.loading = 'eager';
        var pre = new Image(); pre.src = img.currentSrc || img.src;
      });
    }
    // 20 Oct 2026 (speed): warming 250 ms after load put seven slide downloads
    // beside the hero on a 4G phone and pushed its paint out by seconds. Wait
    // for an idle moment after load — the hero is on screen by then — and
    // only then touch the rest.
    function warmWhenIdle() {
      if ('requestIdleCallback' in window) window.requestIdleCallback(warmSlides, { timeout: 4000 });
      else setTimeout(warmSlides, 2500);
    }
    if (document.readyState === 'complete') warmWhenIdle();
    else window.addEventListener('load', function () { setTimeout(warmWhenIdle, 1500); });
    if (prevBtn) prevBtn.addEventListener('click', function () { stepView(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { stepView(1); });
    // v4: arrows on the stage itself as well as the rail.
    var stagePrev = root.querySelector('[data-yt-stage-prev]');
    var stageNext = root.querySelector('[data-yt-stage-next]');
    if (stagePrev) stagePrev.addEventListener('click', function () { stepView(-1); });
    if (stageNext) stageNext.addEventListener('click', function () { stepView(1); });

    // ── ADD TO CART ───────────────────────────────────────────────────
    var errorEl = root.querySelector('[data-yt-base-error]');
    var addBtns = Array.prototype.slice.call(root.querySelectorAll('[data-yt-base-add]'));
    var busy = false;

    // ── 9 Oct 2026 (Lucas): "N in basket" ─────────────────────────────
    // What THIS page has put in the basket, for this visit only (a fresh
    // visit starts from scratch). While the basket holds exactly the plan
    // and add-ons shown, the Add button becomes a bin · "1 in basket" · +
    // control; change an add-on or the plan and it reads "Update basket",
    // and that update REPLACES the lines this page added — never a second
    // copy. (Before: every press added another set, and the sticky
    // button's price span was flattened by the first press, so the price
    // stopped updating after it.)
    var inCart = null; // { bundleKey, configKey, lineKeys: [], qty }
    function configKey() { return plan + '|' + addonsKey(addons); }
    async function cartJson() {
      try { var r = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }); return r.ok ? r.json() : null; } catch (_e) { return null; }
    }
    function linesFor(cart, bundleKey) {
      return ((cart && cart.items) || []).filter(function (i) { return i.properties && i.properties._rc_bundle === bundleKey; });
    }
    // 15 Oct 2026 (order #1303: five Rest-ezy subscriptions in one order).
    // The basket outlives the page. A customer who presses Add, leaves, comes
    // back and presses again lands a SECOND group of the same base, because
    // this page only ever knew about the group it created itself. Every
    // group that contains this base's own product line is "this blend in the
    // basket", whichever visit put it there.
    function groupsForBase(cart) {
      var items = (cart && cart.items) || [];
      var keys = {};
      items.forEach(function (i) {
        if (i.handle === 'base-' + slug && i.properties && i.properties._rc_bundle) keys[i.properties._rc_bundle] = true;
      });
      return Object.keys(keys);
    }
    function linesForBase(cart) {
      var keys = groupsForBase(cart);
      return ((cart && cart.items) || []).filter(function (i) { return i.properties && keys.indexOf(i.properties._rc_bundle) !== -1; });
    }
    async function syncFromCart() {
      var cart = await cartJson();
      var lines = inCart ? linesFor(cart, inCart.bundleKey) : [];
      if (!lines.length) {
        // Nothing from this page — but a group from an earlier visit counts
        // too (15 Oct 2026). Adopt the newest (Shopify prepends, so first).
        var keys = groupsForBase(cart);
        if (keys.length) {
          lines = linesFor(cart, keys[0]);
          inCart = { bundleKey: keys[0], configKey: configKey(), lineKeys: [], qty: 1 };
        } else { inCart = null; }
      }
      if (inCart) { inCart.lineKeys = lines.map(function (l) { return l.key; }); inCart.qty = lines.reduce(function (m, l) { return Math.max(m, l.quantity || 1); }, 1); }
      paintCta();
    }
    syncFromCart();
    async function updateLines(keys, qty) {
      var updates = {}; keys.forEach(function (k) { updates[k] = qty; });
      var r = await fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: updates }) });
      if (!r.ok) throw new Error('Something went wrong updating your basket — please try again.');
      if (window.__ytCartDrawer && typeof window.__ytCartDrawer.render === 'function') { try { await window.__ytCartDrawer.render(); } catch (_e) {} }
    }
    var BIN_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/></svg>';
    var ctls = addBtns.map(function (b) {
      var c = document.createElement('div');
      c.className = 'ybp-incart' + (b.classList.contains('ybp-sticky__cta') ? ' ybp-incart--sticky' : '');
      c.hidden = true;
      c.innerHTML = '<button type="button" class="ybp-incart__bin" aria-label="Remove from basket">' + BIN_SVG + '</button>' +
        '<button type="button" class="ybp-incart__label" aria-label="Open basket"><span><span data-yt-incart-n>1</span> in basket</span></button>' +
        '<button type="button" class="ybp-incart__plus" aria-label="Add one more">+</button>';
      b.insertAdjacentElement('afterend', c);
      c.querySelector('.ybp-incart__bin').addEventListener('click', async function () {
        if (busy || !inCart) return; busy = true;
        try { await updateLines(inCart.lineKeys, 0); inCart = null; if (window.ytTrack) { try { ytTrack('base_landing_removed', { base: slug }); } catch (_e) {} } }
        catch (e) { showError(e.message); }
        busy = false; paintCta();
      });
      c.querySelector('.ybp-incart__plus').addEventListener('click', async function () {
        if (busy || !inCart) return; busy = true;
        try { await updateLines(inCart.lineKeys, inCart.qty + 1); inCart.qty += 1; }
        catch (e) { showError(e.message); }
        busy = false; paintCta();
      });
      c.querySelector('.ybp-incart__label').addEventListener('click', function () {
        if (window.__ytCartDrawer && typeof window.__ytCartDrawer.open === 'function') window.__ytCartDrawer.open(); else window.location.assign('/cart');
      });
      return c;
    });
    function paintCta() {
      var matches = !!(inCart && inCart.configKey === configKey());
      addBtns.forEach(function (b, i) {
        b.hidden = matches;
        ctls[i].hidden = !matches;
        var lab = b.querySelector('[data-yt-cta-label]');
        if (lab) lab.textContent = inCart && !matches ? 'Update basket' : (lab.getAttribute('data-yt-cta-label') || lab.textContent);
      });
      root.querySelectorAll('[data-yt-incart-n]').forEach(function (e) { e.textContent = String(inCart ? inCart.qty : 0); });
    }
    addBtns.forEach(function (b) { var lab = b.querySelector('[data-yt-cta-label]'); if (lab) lab.setAttribute('data-yt-cta-label', lab.textContent); });
    // The basket drawer can change or remove these lines; re-read when it closes.
    (function () {
      var dr = document.querySelector('[data-yt-cartdrawer]'); if (!dr || !window.MutationObserver) return;
      var wasOpen = false;
      new MutationObserver(function () { var open = dr.classList.contains('is-open'); if (wasOpen && !open) syncFromCart(); wasOpen = open; }).observe(dr, { attributes: true, attributeFilter: ['class'] });
    })();
    window.addEventListener('pageshow', function (e) { if (e.persisted) syncFromCart(); });
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }
    // 8 Oct 2026 (Lucas): a BASE product page adds to the basket and opens it
    // (the basket is where the "add to your scoop" carousel will live); only
    // an explicit ?checkout=1 arrival, or data-yt-after="checkout" on the
    // section, goes straight to checkout. Custom blends (yt-formula-pdp /
    // yt-bundle-pdp) are separate scripts and still go straight to checkout.
    var afterAdd = (root.getAttribute('data-yt-after') || 'basket').toLowerCase();
    async function addToCart(forceCheckout) {
      if (busy) return;
      busy = true;
      if (errorEl) errorEl.hidden = true;
      addBtns.forEach(function (b) { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      var origLabels = addBtns.map(function (b) { return b.innerHTML; });
      addBtns.forEach(function (b) { b.textContent = 'ADDING…'; });
      try {
        // 1. The formula. A ?blend= arrival with untouched add-ons reuses
        //    its formula (the builder just minted it); anything else mints
        //    fresh — base + add-ons (add-ons only in the payload; the server
        //    stores the combined snapshot). A fork keeps the blend's name.
        var formulaId = null;
        // 14 Oct 2026 (add-to-basket took 5-7 s): the basket opens NOW with
        // the blend pencilled in, and fills in when Shopify confirms. The
        // bundle the submit returns is used directly — the extra /pdp round
        // trip (and its second Recharge registration) is gone.
        var payload = null;
        if (window.__ytCartDrawer && typeof window.__ytCartDrawer.openPending === 'function' && !(forceCheckout === true || afterAdd === 'checkout')) {
          try { window.__ytCartDrawer.openPending({ name: (loadedName || base.tin_name || base.name || 'Your blend'), price: btnPriceText() }); } catch (_e) {}
        }
        if (loadedFormulaId && addonsKey(addons) === loadedKey) {
          formulaId = loadedFormulaId;
        } else {
          var submitRes = await fetch(PROXY + '/formula/submit', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Idempotency-Key': uuidv4() },
            body: JSON.stringify({
              stack: {
                // Founder 2026-08-28: the tin carries the base's name by
                // default ("Sleep" for the sleep base); 'My formula' is the
                // no-base default only. A preloaded blend keeps its name.
                // Tin default = the base's short, gender-free tin_name (founder 2026-09-02), else its full name.
                stack_name: (loadedName || base.tin_name || base.name || 'My formula').slice(0, 60),
                primary_goals: [],
                ingredients: addons.map(function (a) { return { ingredient_id: a.ingredient_id, amount_mg: a.amount_mg }; }),
              },
              base_slug: slug,
            }),
          });
          if (!submitRes.ok) {
            var detail = 'Something went wrong — please try again.';
            try {
              var err = await submitRes.json();
              if (err && err.error) {
                detail = (err.error.details && err.error.details.message_for_human) || err.error.message || detail;
              }
            } catch (_e) {}
            throw new Error(detail);
          }
          var submitted = await submitRes.json();
          formulaId = submitted.formula_id;
          if (submitted.bundle && Array.isArray(submitted.bundle.items) && submitted.bundle.items.length) payload = { bundle: submitted.bundle };
        }
        if (!formulaId) throw new Error('Something went wrong — please try again.');

        // 2. The registered bundle lines for every plan — only fetched when
        //    the submit did not already hand them over (a ?blend= reuse).
        if (!payload) {
          var pdpRes = await fetch(PROXY + '/formula/' + encodeURIComponent(formulaId) + '/pdp', {
            credentials: 'same-origin', headers: { Accept: 'application/json' },
          });
          if (!pdpRes.ok) throw new Error('Something went wrong preparing your order — please try again.');
          payload = await pdpRes.json();
        }
        if (!payload.bundle || !Array.isArray(payload.bundle.items) || !payload.bundle.items.length) {
          throw new Error('Something went wrong preparing your order — please try again.');
        }

        // 3. Cart lines — identical mapping to the blend PDP: 90-day twin
        //    set with its own key; one-time = 30-day set, plan-less, ':ot'.
        var has90 = !!(payload.bundle.items_90d && payload.bundle.items_90d.length && payload.bundle.bundle_key_90d &&
          payload.bundle.items_90d.every(function (i) { return i.variant_id; }) &&
          payload.bundle.items_90d.some(function (i) { return i.selling_plan_90d; }));
        var isOneTime = plan === 'onetime';
        var is90Add = plan === '3mo' && has90;
        // 9 Oct 2026: `_yt_formula` on every line — the basket drawer's "Add
        // to your scoop" reads the blend's recipe from it to re-mint.
        var props = Object.assign({}, payload.bundle.properties || {}, { _yt_formula: formulaId });
        if (is90Add) {
          props = Object.assign({}, props, { _rc_bundle: payload.bundle.bundle_key_90d });
        } else if (isOneTime && props._rc_bundle) {
          props = Object.assign({}, props, { _rc_bundle: props._rc_bundle + ':ot' });
        }
        // One-time 30-day test: stamp every line so the discount Function
        // prices it at the subscription price and ships it free.
        if (isOneTime && OT_MODE) props = Object.assign({}, props, { _yt_offer: 'ot30' });
        var sourceItems = is90Add ? payload.bundle.items_90d : payload.bundle.items;
        // 9 Oct 2026: the free spoon is first-order only — a signed-in repeat customer does not get it in the cart.
        if (window.__ytRepeatCustomer === true && window.__ytSpoonVariantId) { sourceItems = sourceItems.filter(function (i) { return Number(i.variant_id) !== Number(window.__ytSpoonVariantId); }); }
        var items = sourceItems.map(function (i) {
          var line = { id: Number(i.variant_id), quantity: i.quantity || 1, properties: props };
          if (is90Add && i.selling_plan_90d) line.selling_plan = Number(i.selling_plan_90d);
          else if (!is90Add && !isOneTime && i.selling_plan_30d) line.selling_plan = Number(i.selling_plan_30d);
          return line;
        });
        // An earlier press on THIS page put a set in the basket: take it out
        // first, so an updated plan or add-on replaces it rather than stacking.
        // ...and so does any group of this base from an earlier visit
        // (15 Oct 2026, #1303): the add REPLACES this blend in the basket, it
        // never stacks a second copy of it.
        {
          var stale = linesForBase(await cartJson()).map(function (l) { return l.key; });
          if (stale.length) await updateLines(stale, 0);
          inCart = null;
        }
        // Shopify PREPENDS new lines — submit reversed so the cart displays
        // in payload order (base first, add-ons, then fee/spoon).
        var cartRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: items.slice().reverse() }),
        });
        if (!cartRes.ok) {
          var cdetail = 'Something went wrong adding your blend — please try again.';
          try {
            var cerr = await cartRes.json();
            if (cerr && (cerr.description || cerr.message)) cdetail = cerr.description || cerr.message;
          } catch (_e) {}
          throw new Error(cdetail);
        }
        if (window.ytTrack) { try { ytTrack('base_landing_added', { base: slug, addons: addons.length, plan: plan }); } catch (_e) {} }
        inCart = { bundleKey: props._rc_bundle, configKey: configKey(), lineKeys: [], qty: 1 };
        var nowCart = await cartJson();
        var mine = linesFor(nowCart, props._rc_bundle);
        inCart.lineKeys = mine.map(function (l) { return l.key; });
        inCart.qty = mine.reduce(function (m, l) { return Math.max(m, l.quantity || 1); }, 1);
        if (forceCheckout === true || afterAdd === 'checkout') {
          // Lucas, 26 Sep 2026: straight to checkout, no drawer in between.
          window.location.assign('/checkout');
          return;
        }
        if (window.__ytCartDrawer && typeof window.__ytCartDrawer.open === 'function') {
          addBtns.forEach(function (b, i) { b.disabled = false; b.removeAttribute('aria-busy'); b.innerHTML = origLabels[i]; });
          paintCta();
          await window.__ytCartDrawer.open();
          busy = false;
        } else {
          window.location.assign('/cart');
        }
      } catch (e) {
        if (window.__ytCartDrawer && typeof window.__ytCartDrawer.cancelPending === 'function') { try { window.__ytCartDrawer.cancelPending(); } catch (_e) {} }
        showError(e && e.message ? e.message : 'Something went wrong — please try again.');
        busy = false;
        addBtns.forEach(function (b, i) { b.disabled = false; b.removeAttribute('aria-busy'); b.innerHTML = origLabels[i]; });
      }
    }
    function btnPriceText() {
      var b = addBtns[0]; var m = b && /£\s?[\d.,]+/.exec(b.textContent || '');
      return m ? m[0] : '';
    }
    addBtns.forEach(function (b) { b.addEventListener('click', addToCart); });

    // Carousel arrows (founder 2026-08-28): step one card-and-a-half.
    var prevArrow = root.querySelector('[data-yt-upsell-prev]');
    var nextArrow = root.querySelector('[data-yt-upsell-next]');
    function stepCarousel(dir) {
      if (!gridEl) return;
      var card = gridEl.querySelector('.yt-base-landing__card');
      var w = card ? card.getBoundingClientRect().width + 12 : 220;
      gridEl.scrollBy({ left: dir * w * 1.5, behavior: 'smooth' });
    }
    function syncArrows() {
      if (!gridEl) return;
      var max = gridEl.scrollWidth - gridEl.clientWidth - 2;
      if (prevArrow) prevArrow.disabled = gridEl.scrollLeft <= 2;
      if (nextArrow) nextArrow.disabled = gridEl.scrollLeft >= max;
    }
    if (prevArrow) prevArrow.addEventListener('click', function () { stepCarousel(-1); });
    if (nextArrow) nextArrow.addEventListener('click', function () { stepCarousel(1); });
    if (gridEl) gridEl.addEventListener('scroll', syncArrows, { passive: true });

    buildUpsells();
    syncArrows();
    render();

    // v4 (1 Oct 2026): "Ships by <day>" — today plus the section's
    // ship-within days (first orders ship within 5, per the FAQ), skipping
    // to Monday if that lands on a weekend. Computed here, not in Liquid,
    // because Shopify caches the rendered page.
    (function () {
      var el = root.querySelector('[data-yt-ships-by]');
      if (!el) return;
      var days = Number(root.getAttribute('data-yt-ship-days')) || 5;
      var d = new Date();
      d.setDate(d.getDate() + days);
      if (d.getDay() === 6) d.setDate(d.getDate() + 2);
      else if (d.getDay() === 0) d.setDate(d.getDate() + 1);
      el.textContent = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    })();
    var copyBtn = root.querySelector('[data-yt-copy-code]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var code = copyBtn.getAttribute('data-yt-copy-code') || '';
        try { navigator.clipboard.writeText(code); } catch (_e) {}
        copyBtn.classList.add('is-copied');
        setTimeout(function () { copyBtn.classList.remove('is-copied'); }, 1500);
      });
    }

    // 27 Sep 2026 (audit): a landing page's Checkout button arrives here
    // with ?checkout=1 — the 30-day plan is already selected, so go
    // straight through the same add-and-checkout the button runs.
    if (new URLSearchParams(window.location.search).get('checkout') === '1') {
      setTimeout(function () { addToCart(true); }, 50);
    }

    // Custom-named blend (founder 2026-08-28): "<Name> - <headline>" — the
    // same treatment as the blend PDP, on this page's own H1.
    if (loadedName) {
      paintTinName(loadedName);
      var isDefaultName = /^my formula$/i.test(loadedName) ||
        loadedName.toLowerCase() === String(base.name || '').trim().toLowerCase();
      if (!isDefaultName) {
        var h1 = root.querySelector('.yt-formula-pdp__desc-title');
        if (h1) {
          var headline = h1.textContent.trim();
          h1.textContent = '';
          h1.appendChild(document.createTextNode(loadedName + ' '));
          var sfx = document.createElement('span');
          sfx.className = 'yt-formula-pdp__desc-title-suffix';
          sfx.textContent = '- ' + headline;
          h1.appendChild(sfx);
          document.title = loadedName + ' - ' + headline + ' — Yutrients';
        }
      }
    }
    // Desktop sticky bar (2026-09-21 audit). ADD TO CART computes at y=904
    // on a 1440x900 viewport, and a real laptop browser gives about 780px of
    // page — so the widest screens were the only ones with no purchase
    // control in view, the bar having been hidden outright at >=1024px.
    // It comes back, but only once the real button has scrolled past, so the
    // two never compete. Mobile is untouched: the bar is always on there and
    // `is-visible` has no effect below 1024px.
    //
    // A scroll listener rather than an IntersectionObserver: the observer
    // reported its first entry and then went quiet across repeated scrolls
    // when this was measured on the live page, and a rAF-throttled read of
    // one getBoundingClientRect is cheap enough not to matter.
    (function () {
      var bar = root.querySelector('[data-yt-sticky-bar]');
      var btn = root.querySelector('.yt-formula-pdp__cta--add:not(.yt-base-landing__stickybar-add)');
      if (!bar || !btn) return;
      var ticking = false;
      function read() {
        ticking = false;
        // Past it, not before it: showing the bar on the way down would cover
        // the plan cards the visitor is still reading.
        bar.classList.toggle('is-visible', btn.getBoundingClientRect().bottom < 0);
      }
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(read);
      }, { passive: true });
      read();
    })();
    if (window.ytTrack) { try { ytTrack('base_landing_viewed', { base: slug }); } catch (_e) {} }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
